import { inject, Injectable } from '@angular/core';
import { flatten, isEqual, uniq } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable, Subscriber } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import {
  TrialInsightsPaymentMilestoneResponseType,
  TrialInsightsState,
} from '@models/trial-insights/trial-insights-store.model';
import { TrialInsightsQuery } from '@models/trial-insights/trial-insights.query';

import { SortOrder } from '@shared/services/gql.service';
import { GenericOperator } from '@shared/services/operator.service';
import { Utils } from '@shared/utils/utils';

import {
  TIRSChartLabel,
  TIRSChartType,
  TrialInsightsRemainingSpendChartService,
} from './remaining-spend-over-time-chart.service';
import { TrialInsightsRemainingSpendTableService } from './remaining-spend-over-time-table.service';
import { RemainingSpendOverTimeConstants } from './remaining-spend-over-time.const';
import {
  GenericTrialInsightsQuery,
  SortOrderDefault,
} from '../../classes/trial-insights-query.class';
import {
  TrialInsightsRemainingSpendKey,
  TrialInsightsTableRowData,
} from '../../models/trial-insights-table.model';

type Results = [State['data'], State['totalRemainingSpend'], TrialInsightsRemainingSpendKey];
type State = Partial<TrialInsightsState['remainingSpend']>;

const sortDefaults: SortOrderDefault[] = [
  {
    buttonKey: TrialInsightsRemainingSpendKey.REMAINING_SPEND,
    defaultOrder: SortOrder.DESC,
  },
];

@Injectable({
  providedIn: 'root',
})
export class TrialInsightsRemainingSpendQueryService extends GenericTrialInsightsQuery<TIRSChartType> {
  chartService: TrialInsightsRemainingSpendChartService;
  tableService: TrialInsightsRemainingSpendTableService;
  trialInsightsQuery: TrialInsightsQuery;

  constructor() {
    const trialInsightsQuery = inject(TrialInsightsQuery);
    const chartService = inject(TrialInsightsRemainingSpendChartService);
    const tableService = inject(TrialInsightsRemainingSpendTableService);

    super({
      chartService,
      slice: 'remainingSpend',
      sortDefaults,
      tableService,
      trialInsightsQuery,
    });

    this.trialInsightsQuery = trialInsightsQuery;
    this.chartService = chartService;
    this.tableService = tableService;
  }

  processRemainingSpend$(): Observable<Results> {
    return combineLatest([
      this.data as Observable<TrialInsightsPaymentMilestoneResponseType[]>,
      this.trialInsightsQuery.select((state) => state.remainingSpend.totalRemainingSpend),
      this.selectedKey as BehaviorSubject<TrialInsightsRemainingSpendKey>,
    ]).pipe(distinctUntilChanged(isEqual), this.processResponseData());
  }

  processResponseData() {
    const processFn = (sourceValue: Results, subscriber: Subscriber<Results>) => {
      const [data, totalRemainingSpend, selectedKey] = sourceValue;

      this.totalAmount.next(Utils.currencyFormatter(totalRemainingSpend || 0));

      this.renderTable(data || [], selectedKey);
      this.renderChart(data || []);

      subscriber.next(sourceValue);
    };

    const operatorConfig = new GenericOperator(processFn);

    return operatorConfig.operator();
  }

  private renderChart(data: Results[0]): void {
    const remainingList: TIRSChartLabel = [];
    const labels: string[][] = [];

    (data || []).forEach((item) => {
      labels.push(item.remaining_spend_time_series.map(({ period }) => period));
      remainingList.push(item.remaining_spend_time_series.map(({ amount }) => amount));
    });

    const datasets = this.chartService.createDatasets(remainingList);

    const sortedQuarters = uniq(flatten(labels))
      .sort((a, b) => Utils.dateSort(a, b, true))
      .map((date) => Utils.getQuarterDateString(date));

    const chartOptions = this.chartService.createChart(datasets, sortedQuarters);

    this.chartOptions.next(chartOptions);
  }

  private renderTable(data: Results[0], selectedKey: TrialInsightsRemainingSpendKey): void {
    const tableData: TrialInsightsTableRowData[] = (data || []).map((item, index) => {
      return {
        buttonKey: TrialInsightsRemainingSpendKey.REMAINING_SPEND,
        color:
          RemainingSpendOverTimeConstants.chartLineColor[index] ||
          RemainingSpendOverTimeConstants.chartLineColor[0],
        leftHeader: item.entity_name,
        leftSubheader: '',
        rightHeader: Utils.currencyFormatter(item.remaining_spend.amount),
        rightSubheader: Utils.percentageFormatter(item.remaining_spend.percentage || 0),
      };
    });

    const tableOptions = this.tableService.createTable(selectedKey, tableData);

    this.tableOptions.next(tableOptions);
  }
}
